import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, FileText, ArrowUpDown, ArrowUp, ArrowDown, Pencil, RotateCcw, TrendingUp, TrendingDown, Minus, Package, Clock, Settings2, ChevronDown, ChevronUp, Target, MessageSquareText, Sparkles, Loader2, ExternalLink, History, Download, Trash2, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIAS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const LS_SEMANAS = 'rankingFinal.semanas';
const LS_DIAS = 'rankingFinal.dias';
const MSG_MAX = 250; // limite da mensagem à equipa (cabe no cartaz PDF)

const fmtISO = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const toMonday = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return fmtISO(d);
};
const addDays = (iso, n) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return fmtISO(d);
};
const fmtVal = (v, suf = '%') => (v === null || v === undefined ? '—' : `${v}${suf}`);
const fmtHoras = (h) => (h === null || h === undefined ? '—' : `${Math.floor(h)}h ${String(Math.round((h - Math.floor(h)) * 60)).padStart(2, '0')}m`);
const loadLS = (k, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(k)); return Array.isArray(v) && v.length ? v : fallback; } catch { return fallback; }
};

const NivelBadge = ({ nivel, valor, className = '' }) => {
  if (valor === null || valor === undefined) return <span className={`text-stone-400 ${className}`}>—</span>;
  const style = nivel ? { backgroundColor: nivel.cor, color: nivel.texto } : {};
  return (
    <span className={`inline-flex items-center justify-center min-w-[3.5rem] px-2 py-0.5 rounded-md font-bold text-sm ${className}`} style={style} title={nivel ? `Nível ${nivel.nivel} — ${nivel.nome}` : ''}>
      {valor}%
    </span>
  );
};

const CelulaDia = ({ celula, incluido, onClick, testId }) => {
  const vazio = celula.eficiencia_final === null && celula.ocupacao_final === null;
  const sugerido = celula.eficiencia_sugerida !== null || celula.ocupacao_sugerida !== null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={sugerido ? `Valor sugerido${celula.motivo ? ` — ${celula.motivo}` : ''}\nReal: ${fmtVal(celula.eficiencia_real)} / ${fmtVal(celula.ocupacao_real)}\n\nClique para editar` : 'Clique para editar / sugerir um valor'}
      className={`relative w-full h-full min-h-[3.2rem] px-1.5 py-1 rounded-lg border text-center transition-colors group cursor-pointer ${
        sugerido ? 'bg-amber-50 border-amber-300 hover:bg-amber-100 hover:border-amber-400' : vazio ? 'bg-stone-50 border-dashed border-stone-200 hover:bg-emerald-50 hover:border-emerald-300' : 'bg-white border-stone-200 hover:bg-emerald-50 hover:border-emerald-300'
      } ${incluido ? '' : 'opacity-40'}`}
    >
      {/* Ícone de edição — sempre visível (subtil quando não em hover) */}
      <Pencil
        className={`absolute top-1 right-1 h-3 w-3 transition-opacity ${
          sugerido ? 'text-amber-600 opacity-90' : 'text-stone-400 opacity-40 group-hover:opacity-100 group-hover:text-emerald-600'
        }`}
      />
      {vazio ? (
        <span className="text-stone-300 text-xs">—</span>
      ) : (
        <>
          <div className={`text-sm font-bold leading-tight ${sugerido ? 'text-amber-700' : 'text-navy'}`}>
            {fmtVal(celula.eficiencia_final, '')}<span className="text-[9px] font-medium text-stone-400">%</span>
          </div>
          <div className="text-[10px] text-stone-500 leading-tight">
            ocup. <span className={`font-semibold ${celula.ocupacao_sugerida !== null ? 'text-amber-700' : 'text-stone-700'}`}>{fmtVal(celula.ocupacao_final, '')}%</span>
          </div>
          <div className="text-[9px] text-emerald-700 leading-tight">{celula.pecas_acabadas} pç</div>
        </>
      )}
    </button>
  );
};

const AjusteDialog = ({ ajuste, onClose, onSaved }) => {
  const [efic, setEfic] = useState('');
  const [ocup, setOcup] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ajuste) return;
    setEfic(ajuste.celula.eficiencia_sugerida ?? '');
    setOcup(ajuste.celula.ocupacao_sugerida ?? '');
    setMotivo(ajuste.celula.motivo ?? '');
  }, [ajuste]);

  if (!ajuste) return null;
  const { celula, operador, data } = ajuste;

  const verRegistosDia = () => {
    const qs = new URLSearchParams({ tab: 'operador', operador: operador.id, data });
    window.open(`/gestor/relatorios?${qs.toString()}`, '_blank', 'noopener');
  };

  const guardar = async (repor = false) => {
    const semValores = efic === '' && ocup === '';
    const temSugestao = celula.eficiencia_sugerida !== null || celula.ocupacao_sugerida !== null;
    // Guardar com os 2 campos vazios = sem sugestão. Se já havia uma, equivale a "Repor real";
    // se não havia, não há nada para guardar (antes mostrava "Sugestão guardada" sem guardar nada).
    if (!repor && semValores) {
      if (!temSugestao) { toast.error('Indique a eficiência e/ou a ocupação a sugerir'); return; }
      repor = true;
    }
    if (!repor && ocup !== '' && (Number(ocup) < 0 || Number(ocup) > 100)) { toast.error('A ocupação deve estar entre 0 e 100%'); return; }
    if (!repor && efic !== '' && (Number(efic) < 0 || Number(efic) > 500)) { toast.error('A eficiência deve estar entre 0 e 500%'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/relatorios/ranking-final/ajuste`, {
        operador_id: operador.id,
        data,
        eficiencia: repor || efic === '' ? null : Number(efic),
        ocupacao: repor || ocup === '' ? null : Number(ocup),
        motivo: repor ? null : motivo,
      });
      toast.success(repor ? 'Valor real reposto' : 'Sugestão guardada');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ajuste-dialog">
        <DialogHeader>
          <DialogTitle>Sugerir valor — {operador.nome}</DialogTitle>
          <p className="text-sm text-stone-500">{DIAS[(new Date(data + 'T00:00:00').getDay() + 6) % 7]} {data.slice(8, 10)}/{data.slice(5, 7)}/{data.slice(0, 4)}</p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Eficiência (%)</Label>
            <p className="text-[11px] text-stone-500 mb-1">Real: <strong>{fmtVal(celula.eficiencia_real)}</strong></p>
            <Input type="number" min="0" max="500" step="0.1" value={efic} onChange={(e) => setEfic(e.target.value)} placeholder="manter real" data-testid="ajuste-eficiencia-input" />
          </div>
          <div>
            <Label className="text-xs">Ocupação (%)</Label>
            <p className="text-[11px] text-stone-500 mb-1">Real: <strong>{fmtVal(celula.ocupacao_real)}</strong></p>
            <Input type="number" min="0" max="100" step="0.1" value={ocup} onChange={(e) => setOcup(e.target.value)} placeholder="manter real" data-testid="ajuste-ocupacao-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Motivo (opcional)</Label>
          <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="ex: avaria de máquina, formação..." data-testid="ajuste-motivo-input" />
        </div>
        <p className="text-[11px] text-stone-500">Campos vazios mantêm o valor real. A sugestão substitui o valor real no cálculo da semana.</p>
        {/* Atalho: abrir o relatório do operador filtrado neste dia para perceber o motivo */}
        <div className="flex items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
          <p className="text-[11px] text-sky-900 leading-snug">
            Porquê este valor? Vê as operações, pausas e tempos de <strong>{operador.nome}</strong> neste dia.
            {celula.tem_dados && <span className="block text-sky-700">{celula.pecas} peças · {fmtHoras(celula.horas)} em operações</span>}
          </p>
          <Button type="button" variant="outline" size="sm" className="shrink-0 h-8 border-sky-300 text-sky-800 hover:bg-sky-100" onClick={verRegistosDia} data-testid="ajuste-ver-registos-btn" title="Abre o relatório Por Operador filtrado por este operador e dia (numa nova aba)">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver registos do dia
          </Button>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={() => guardar(true)} disabled={saving || (celula.eficiencia_sugerida === null && celula.ocupacao_sugerida === null)} data-testid="ajuste-repor-btn">
            <RotateCcw className="h-4 w-4 mr-1" /> Repor real
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button size="sm" onClick={() => guardar(false)} disabled={saving} data-testid="ajuste-guardar-btn">Guardar</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Cabeçalho ordenável (fora do SemanaTabela para não ser recriado a cada render)
const ThOrdenavel = ({ k, sort, onSort, children, className = '' }) => {
  const ativo = sort.key === k;
  const Icon = !ativo ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-2 py-2 text-[11px] font-semibold text-stone-600 select-none ${className}`}>
      <button type="button" onClick={() => onSort(k)} className={`inline-flex items-center gap-1 hover:text-navy ${ativo ? 'text-navy' : ''}`} data-testid={`rf-sort-${k.replace(':', '-')}`}>
        {children}<Icon className="h-3 w-3" />
      </button>
    </th>
  );
};

const SemanaTabela = ({ semana, sort, onSort, onEditar, opsAcabadas, index = 0, total = 1 }) => {
  const operadores = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (o) => {
      if (sort.key === 'nome') return o.nome.toLowerCase();
      if (sort.key === 'pecas_acabadas') return o.semana.pecas_acabadas ?? -1;
      if (sort.key === 'ocupacao') return o.semana.ocupacao ?? -1;
      if (sort.key.startsWith('dia:')) return o.dias[sort.key.slice(4)]?.eficiencia_final ?? -1;
      return o.semana.eficiencia ?? -1;
    };
    return [...semana.operadores].sort((a, b) => (get(a) > get(b) ? dir : get(a) < get(b) ? -dir : 0));
  }, [semana, sort]);

  const eq = semana.equipa;
  const ACENTOS = ['#1e3a5f', '#0e7490', '#7c3aed', '#b45309', '#15803d', '#be123c', '#4f46e5'];
  const acento = ACENTOS[index % ACENTOS.length];
  return (
    <Card className="p-4 border-l-[6px] shadow-md" style={{ borderLeftColor: acento }} data-testid={`rf-semana-${semana.inicio}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[11px] font-bold text-white shrink-0" style={{ backgroundColor: acento }} data-testid={`rf-semana-badge-${semana.inicio}`}>
              Semana {index + 1} de {total}
            </span>
            <h3 className="text-base font-semibold" style={{ color: acento }}>{semana.label}</h3>
          </div>
          <p className="text-xs text-stone-500">{eq.operadores} operadores · {eq.dias_uteis} dias úteis com actividade</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-stone-500">Eficiência equipa</p>
            <NivelBadge nivel={eq.nivel} valor={eq.eficiencia} className="text-base" />
            {eq.nivel && <p className="text-[10px] text-stone-500 mt-0.5">Nível {eq.nivel.nivel} · {eq.nivel.nome}</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-stone-500">Ocupação</p>
            <p className="text-base font-bold text-emerald-700">{fmtVal(eq.ocupacao)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-stone-500">Peças acabadas</p>
            <p className="text-base font-bold text-navy flex items-center justify-end gap-1" data-testid={`rf-equipa-acab-${semana.inicio}`}><Package className="h-4 w-4 text-emerald-600" />{eq.pecas_acabadas}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-stone-500">Horas em operações</p>
            <p className="text-base font-bold text-navy flex items-center justify-end gap-1" data-testid={`rf-equipa-horas-${semana.inicio}`}><Clock className="h-4 w-4 text-blue-600" />{fmtHoras(eq.horas)}</p>
          </div>
        </div>
      </div>

      <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900" data-testid={`rf-hint-edit-${semana.inicio}`}>
        <Pencil className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
        <div className="leading-snug">
          <strong>Editar valores por dia:</strong> clica em qualquer <em>célula do dia</em> na tabela abaixo para
          ajustar a eficiência ou a ocupação daquele operador nesse dia (com motivo).
          Células com valor sugerido ficam a <span className="inline-block px-1.5 rounded bg-amber-100 border border-amber-300 text-amber-800 font-semibold">amarelo</span> —
          entram na média em vez do valor real.
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-1 min-w-[900px]">
          <thead>
            <tr>
              <ThOrdenavel sort={sort} onSort={onSort} k="nome" className="text-left">Operador</ThOrdenavel>
              {semana.dias.map((d) => (
                <ThOrdenavel sort={sort} onSort={onSort} key={d.data} k={`dia:${d.data}`} className={`text-center ${d.incluido ? '' : 'opacity-40'}`}>
                  <span>{d.label} <span className="font-normal text-stone-400">{d.data.slice(8, 10)}/{d.data.slice(5, 7)}</span></span>
                </ThOrdenavel>
              ))}
              <ThOrdenavel sort={sort} onSort={onSort} k="eficiencia" className="text-center">Efic. semana</ThOrdenavel>
              <ThOrdenavel sort={sort} onSort={onSort} k="ocupacao" className="text-center">Ocup. semana</ThOrdenavel>
              <ThOrdenavel sort={sort} onSort={onSort} k="pecas_acabadas" className="text-center">Peças acab.</ThOrdenavel>
            </tr>
          </thead>
          <tbody>
            {operadores.map((op, idx) => (
              <tr key={op.id} data-testid={`rf-op-row-${op.id}`}>
                <td className="px-2 py-1 whitespace-nowrap">
                  <span className="text-[10px] text-stone-400 mr-1.5">#{idx + 1}</span>
                  <span className="font-medium text-sm text-navy">{op.nome}</span>
                  <p className="text-[10px] text-stone-400">{op.semana.dias_com_dados} dia(s) no cálculo</p>
                </td>
                {semana.dias.map((d) => (
                  <td key={d.data} className="px-0.5 py-0.5 w-[6.5rem]">
                    <CelulaDia celula={op.dias[d.data]} incluido={d.incluido} onClick={() => onEditar({ operador: op, data: d.data, celula: op.dias[d.data] })} testId={`rf-cell-${op.id}-${d.data}`} />
                  </td>
                ))}
                <td className="px-2 text-center" data-testid={`rf-week-efic-${op.id}`}><NivelBadge nivel={op.semana.nivel} valor={op.semana.eficiencia} /></td>
                <td className="px-2 text-center font-semibold text-emerald-700 text-sm">{fmtVal(op.semana.ocupacao)}</td>
                <td className="px-2 text-center font-semibold text-navy text-sm">{op.semana.pecas_acabadas}</td>
              </tr>
            ))}
            {operadores.length === 0 && (
              <tr><td colSpan={11} className="text-center text-stone-500 py-6 text-sm">Sem registos nesta semana</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-stone-50">
              <td className="px-2 py-2 text-xs font-bold text-navy rounded-l-lg">EQUIPA (média)</td>
              {semana.dias.map((d) => (
                <td key={d.data} className={`px-1 py-2 text-center ${d.incluido ? '' : 'opacity-40'}`}>
                  <div className="text-sm font-bold text-navy">{fmtVal(d.eficiencia, '')}<span className="text-[9px] text-stone-400">%</span></div>
                  <div className="text-[10px] text-stone-500">ocup. {fmtVal(d.ocupacao, '')}%</div>
                </td>
              ))}
              <td className="px-2 text-center"><NivelBadge nivel={eq.nivel} valor={eq.eficiencia} /></td>
              <td className="px-2 text-center font-bold text-emerald-700 text-sm">{fmtVal(eq.ocupacao)}</td>
              <td className="px-2 text-center font-bold text-navy text-sm rounded-r-lg">{eq.pecas_acabadas}</td>
            </tr>
            <tr className="bg-emerald-50/60">
              <td className="px-2 py-1.5 text-[11px] font-semibold text-emerald-800 rounded-l-lg">Peças acabadas/dia <span className="font-normal text-emerald-600">({(opsAcabadas || []).join('+')})/{(opsAcabadas || []).length || 1}</span></td>
              {semana.dias.map((d) => (
                <td key={d.data} className={`px-1 text-center text-sm font-semibold text-emerald-800 ${d.incluido ? '' : 'opacity-40'}`} data-testid={`rf-team-acab-${d.data}`}>{d.pecas_acabadas}</td>
              ))}
              <td colSpan={3} className="px-2 text-right text-xs text-emerald-800 rounded-r-lg">Total semana: <strong>{eq.pecas_acabadas}</strong> · {eq.pecas} peças no total</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
};

const GraficoProdutividade = ({ semanas, metaAlvo }) => {
  const data = semanas.map((s) => ({ label: s.label.replace(/\/\d{4}$/, ''), valor: s.equipa.eficiencia, cor: s.equipa.nivel?.cor || '#1e3a5f' }));
  const maxV = Math.max(100, metaAlvo || 0, ...data.map((d) => d.valor || 0));
  return (
    <div className="h-64 w-full" data-testid="rf-grafico-produtividade">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 18, right: 16, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#57534e' }} interval={0} />
          <YAxis domain={[0, Math.ceil(maxV / 20) * 20]} tick={{ fontSize: 10, fill: '#57534e' }} />
          <Tooltip formatter={(v) => [`${v}%`, 'Eficiência equipa']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          {metaAlvo ? <ReferenceLine y={metaAlvo} stroke="#dc2626" strokeDasharray="4 3" label={{ value: `Meta ${metaAlvo}%`, position: 'insideTopRight', fontSize: 10, fill: '#dc2626' }} /> : null}
          <Bar dataKey="valor" radius={[4, 4, 0, 0]} maxBarSize={56} isAnimationActive={false}>
            {data.map((d) => <Cell key={d.label} fill={d.cor} />)}
            <LabelList dataKey="valor" position="top" formatter={(v) => (v === null || v === undefined ? '' : `${v}%`)} style={{ fontSize: 11, fontWeight: 700, fill: '#1c1917' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const NIVEIS_LIMITES_DEFAULT = [50, 70, 80, 88, 94, 100];

const faixaTexto = (idx, L) => {
  const fmt = (v) => (Number.isInteger(v) ? String(v) : String(Number(v.toFixed(1))));
  if (L.some((v) => v === '' || Number.isNaN(Number(v)))) return '—';
  const n = L.map(Number);
  const N = n.length; // nº de limites (níveis = N + 1)
  if (idx === 0) return Number.isInteger(n[0]) ? `0 - ${fmt(n[0] - 1)}%` : `< ${fmt(n[0])}%`;
  if (idx >= 1 && idx <= N - 2) return Number.isInteger(n[idx]) ? `${fmt(n[idx - 1])} - ${fmt(n[idx] - 1)}%` : `${fmt(n[idx - 1])} - <${fmt(n[idx])}%`;
  if (idx === N - 1) return `${fmt(n[N - 2])} - ${fmt(n[N - 1])}%`;
  return `> ${fmt(n[N - 1])}%`;
};

// Preto ou branco conforme a luminância da cor de fundo (contraste legível)
const textoParaCor = (hex) => {
  try {
    const h = String(hex).replace('#', '');
    const r = parseInt(h.substr(0, 2), 16);
    const g = parseInt(h.substr(2, 2), 16);
    const b = parseInt(h.substr(4, 2), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? '#1c1917' : '#ffffff';
  } catch {
    return '#1c1917';
  }
};

// Editar as percentagens de cada nível (início de cada nível; Nível -1 começa sempre em 0)
const NiveisDialog = ({ open, onClose, niveis, limites, onSaved }) => {
  const [vals, setVals] = useState(NIVEIS_LIMITES_DEFAULT.map(String));
  const [nomes, setNomes] = useState({});
  const [cores, setCores] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVals((limites && limites.length === NIVEIS_LIMITES_DEFAULT.length ? limites : NIVEIS_LIMITES_DEFAULT).map((v) => String(v)));
    const m = {};
    const c = {};
    (niveis || []).forEach((n) => { m[String(n.nivel)] = n.nome || ''; c[String(n.nivel)] = n.cor || '#cccccc'; });
    setNomes(m);
    setCores(c);
  }, [open, limites, niveis]);

  const nomeVazio = (niveis || []).some((n) => !(nomes[String(n.nivel)] || '').trim());

  const nums = vals.map((v) => (v === '' ? NaN : Number(v)));
  const erro = nums.some((v) => Number.isNaN(v))
    ? 'Preencha todos os limites'
    : nomeVazio
      ? 'Todos os níveis precisam de um nome'
    : nums[0] <= 0 || nums[nums.length - 1] > 500
      ? 'Os limites devem estar entre 1% e 500%'
      : nums.some((v, i) => i > 0 && v <= nums[i - 1])
        ? 'Cada nível tem de começar numa percentagem maior que o anterior'
        : null;

  const guardar = async () => {
    if (erro) { toast.error(erro); return; }
    setSaving(true);
    try {
      const nomesLimpos = {};
      Object.entries(nomes).forEach(([k, v]) => { nomesLimpos[k] = (v || '').trim(); });
      const res = await axios.put(`${API}/relatorios/ranking-final/config`, { niveis_limites: nums, niveis_nomes: nomesLimpos, niveis_cores: cores });
      toast.success('Níveis guardados');
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar níveis');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="rf-niveis-dialog">
        <DialogHeader>
          <DialogTitle>Nomes e percentagens dos níveis</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-stone-500">Clique no nome para o mudar (máx. 30 caracteres). Defina a percentagem em que <strong>começa</strong> cada nível. O nível anterior termina logo abaixo desse valor. O penúltimo nível inclui o último valor e o último nível é tudo o que fica acima.</p>
        <div className="space-y-1.5">
          {(niveis || []).map((n, i) => {
            const corAtual = cores[String(n.nivel)] || n.cor;
            return (
            <div key={n.nivel} className="flex items-center gap-2 text-xs rounded-md overflow-hidden pr-2" style={{ backgroundColor: corAtual, color: textoParaCor(corAtual) }} data-testid={`rf-niveis-row-${n.nivel}`}>
              <span className="px-2 py-1.5 font-bold w-16 shrink-0">Nível {n.nivel}</span>
              <input
                type="color"
                value={corAtual}
                onChange={(e) => setCores({ ...cores, [String(n.nivel)]: e.target.value })}
                title="Escolher a cor deste nível"
                className="h-6 w-8 shrink-0 rounded cursor-pointer border border-white/40 bg-transparent p-0"
                data-testid={`rf-niveis-cor-${n.nivel}`}
              />
              <input
                type="text"
                value={nomes[String(n.nivel)] ?? ''}
                maxLength={30}
                onChange={(e) => setNomes({ ...nomes, [String(n.nivel)]: e.target.value })}
                placeholder={n.nome_origem || ''}
                title={n.nome_origem && n.nome_origem !== nomes[String(n.nivel)] ? `Nome de origem: ${n.nome_origem}` : 'Mudar o nome do nível'}
                className="w-32 shrink-0 h-7 px-1.5 rounded bg-white/25 hover:bg-white/40 [color:inherit] focus:bg-white focus:text-stone-900 border border-transparent focus:border-stone-300 outline-none font-semibold uppercase text-xs placeholder:normal-case placeholder:opacity-60"
                data-testid={`rf-niveis-nome-${n.nivel}`}
              />
              <span className="flex-1 flex items-center gap-1 justify-end">
                {i === 0 ? (
                  <span className="opacity-80">desde 0%</span>
                ) : i === vals.length ? (
                  <span className="opacity-80">acima de {vals[vals.length - 1] || '—'}%</span>
                ) : (
                  <>
                    <span className="opacity-80 whitespace-nowrap">{i === vals.length - 1 ? 'de' : 'começa em'}</span>
                    <Input type="number" min={1} max={500} step={1} value={vals[i - 1]}
                      onChange={(e) => setVals(vals.map((v, j) => (j === i - 1 ? e.target.value : v)))}
                      className="h-7 w-16 text-xs text-center bg-white text-stone-900" data-testid={`rf-niveis-input-${n.nivel}`} />
                    <span>%</span>
                  </>
                )}
                {i === vals.length - 1 && (
                  <>
                    <span className="opacity-80 ml-1">até</span>
                    <Input type="number" min={1} max={500} step={1} value={vals[vals.length - 1]}
                      onChange={(e) => setVals(vals.map((v, j) => (j === vals.length - 1 ? e.target.value : v)))}
                      className="h-7 w-16 text-xs text-center bg-white text-stone-900" data-testid={`rf-niveis-input-${n.nivel}-max`} />
                    <span>%</span>
                  </>
                )}
              </span>
              <span className="w-20 shrink-0 text-right font-medium tabular-nums" data-testid={`rf-niveis-faixa-${n.nivel}`}>{faixaTexto(i, vals)}</span>
            </div>
            );
          })}
        </div>
        {erro && <p className="text-xs text-red-600" data-testid="rf-niveis-erro">{erro}</p>}
        <p className="text-[11px] text-stone-500">A alteração aplica-se a todas as semanas, ao quadro resumo, às cores da tabela e ao PDF.</p>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" size="sm" onClick={() => {
            setVals(NIVEIS_LIMITES_DEFAULT.map(String));
            const m = {};
            const c = {};
            (niveis || []).forEach((n) => { m[String(n.nivel)] = n.nome_origem || n.nome; c[String(n.nivel)] = n.cor_origem || n.cor; });
            setNomes(m);
            setCores(c);
          }} data-testid="rf-niveis-restaurar-btn">Repor valores de origem</Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="button" onClick={guardar} disabled={saving || !!erro} data-testid="rf-niveis-guardar-btn">{saving ? 'A guardar...' : 'Guardar'}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const QuadroResumo = ({ niveis, config, onSaved }) => {
  const [editar, setEditar] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] uppercase tracking-wide text-stone-500">Quadro resumo</p>
        <button type="button" onClick={() => setEditar(true)} className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-navy" title="Editar nomes e percentagens dos níveis" data-testid="rf-niveis-editar-btn">
          <Pencil className="h-3 w-3" /> Editar
        </button>
      </div>
      <div className="space-y-1">
        {niveis.map((n) => (
          <div key={n.nivel} className="flex items-center text-xs rounded-md overflow-hidden" style={{ backgroundColor: n.cor, color: n.texto }} data-testid={`rf-quadro-nivel-${n.nivel}`}>
            <span className="px-2 py-1 font-bold w-20">Nível {n.nivel}</span>
            <span className="px-2 py-1 w-24">{n.faixa}</span>
            <span className="px-2 py-1 font-semibold uppercase">{n.nome}</span>
          </div>
        ))}
      </div>
      <NiveisDialog open={editar} onClose={() => setEditar(false)} niveis={niveis} limites={config?.niveis_limites} onSaved={onSaved} />
    </div>
  );
};

const ResumoEvolucao = ({ semanas, niveis, config, onSaved }) => (
  <Card className="p-4" data-testid="rf-resumo-evolucao">
    <h3 className="text-base font-semibold text-navy mb-3">Evolução da Produtividade</h3>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
      <div className="lg:col-span-2 rounded-xl border border-stone-100 p-2">
        <p className="text-[11px] uppercase tracking-wide text-stone-500 text-center">Produtividade — eficiência da equipa (%)</p>
        <GraficoProdutividade semanas={semanas} metaAlvo={config?.meta_alvo} />
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl p-3 text-center" style={{ backgroundColor: semanas[semanas.length - 1].equipa.nivel?.cor || '#f5f5f4', color: semanas[semanas.length - 1].equipa.nivel?.texto || '#1c1917' }} data-testid="rf-ultima-meta">
            <p className="text-[10px] uppercase tracking-wide opacity-80">Última meta alcançada</p>
            <p className="text-2xl font-bold">{fmtVal(semanas[semanas.length - 1].equipa.eficiencia)}</p>
          </div>
          <div className="rounded-xl p-3 text-center bg-[#bbd0b1] text-stone-900" data-testid="rf-meta-alvo">
            <p className="text-[10px] uppercase tracking-wide opacity-80 flex items-center justify-center gap-1"><Target className="h-3 w-3" /> Meta alvo</p>
            <p className="text-2xl font-bold">{config?.meta_alvo ? `${config.meta_alvo}%` : '—'}</p>
          </div>
        </div>
        <div className="rounded-xl border border-stone-200 p-3 bg-stone-50" data-testid="rf-mensagem-equipa">
          <p className="text-[10px] uppercase tracking-wide text-stone-500 flex items-center gap-1 mb-1"><MessageSquareText className="h-3 w-3" /> Mensagem à equipa</p>
          <p className="text-sm text-stone-700 whitespace-pre-line">{config?.mensagem_equipa || <span className="text-stone-400 italic">Sem mensagem definida — configure em "Configuração"</span>}</p>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] text-stone-500 uppercase tracking-wide">
              <th className="text-left px-2 py-1">Período</th><th className="px-2">Dias úteis</th><th className="px-2">Eficiência</th><th className="px-2">Ocupação</th><th className="px-2">Peças acab.</th><th className="px-2">Horas op.</th><th className="px-2">Nível</th><th className="px-2">Tend.</th>
            </tr>
          </thead>
          <tbody>
            {semanas.map((s, i) => {
              const prev = semanas.slice(0, i).reverse().find((p) => p.equipa.eficiencia !== null)?.equipa.eficiencia;
              const cur = s.equipa.eficiencia;
              const Tend = prev === undefined || cur === null ? null : cur > prev ? TrendingUp : cur < prev ? TrendingDown : Minus;
              return (
                <tr key={s.inicio} className="border-t border-stone-100">
                  <td className="px-2 py-1.5 font-medium">{s.label}</td>
                  <td className="px-2 text-center">{s.equipa.dias_uteis}</td>
                  <td className="px-2 text-center"><NivelBadge nivel={s.equipa.nivel} valor={cur} /></td>
                  <td className="px-2 text-center text-emerald-700 font-semibold">{fmtVal(s.equipa.ocupacao)}</td>
                  <td className="px-2 text-center font-semibold">{s.equipa.pecas_acabadas}</td>
                  <td className="px-2 text-center text-xs">{fmtHoras(s.equipa.horas)}</td>
                  <td className="px-2 text-center text-xs">{s.equipa.nivel ? `${s.equipa.nivel.nivel} · ${s.equipa.nivel.nome}` : '—'}</td>
                  <td className="px-2 text-center">{Tend && <Tend className={`h-4 w-4 inline ${Tend === TrendingUp ? 'text-emerald-600' : Tend === TrendingDown ? 'text-red-600' : 'text-stone-400'}`} />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <QuadroResumo niveis={niveis} config={config} onSaved={onSaved} />
    </div>
  </Card>
);

const ConfigPanel = ({ config, onSaved, semanas = [], dias = [] }) => {
  const [open, setOpen] = useState(false);
  const [ops, setOps] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [sel, setSel] = useState([]);
  const [meta, setMeta] = useState('');
  const [msg, setMsg] = useState('');
  const [orientacao, setOrientacao] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [tomIA, setTomIA] = useState('motivador');
  const [elapsed, setElapsed] = useState(0);

  // Contador de segundos enquanto a IA gera a mensagem (feedback visível)
  useEffect(() => {
    if (!suggesting) { setElapsed(0); return undefined; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(id);
  }, [suggesting]);

  const sugerirComIA = async () => {
    if (!semanas || semanas.length === 0) {
      toast.error('Selecciona pelo menos uma semana antes de pedir sugestão à IA.');
      return;
    }
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/relatorios/ranking-final/mensagem-ia`, {
        semanas: semanas.join(','),
        dias: dias.join(','),
        tom: tomIA,
        orientacao: orientacao || null,
      }, { timeout: 45000 });
      const nova = res.data?.mensagem || '';
      if (!nova) {
        toast.error('A IA devolveu uma mensagem vazia. Tenta novamente.');
        return;
      }
      setMsg(nova.slice(0, MSG_MAX));
      const seg = res.data?.segundos != null ? ` em ${res.data.segundos}s` : '';
      toast.success(`Sugestão gerada${seg} (${res.data?.model || 'IA'}). Revê e clica em Guardar.`);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Falha ao contactar a IA.';
      if (status === 400 && /configurad/i.test(detail)) {
        toast.error(detail + ' Vai a Configurações → OpenAI.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    if (!config) return;
    setSel(config.operacoes_acabadas || []);
    setMeta(config.meta_alvo ?? '');
    setMsg(config.mensagem_equipa || '');
    setOrientacao(config.orientacao_ia || '');
  }, [config]);

  useEffect(() => {
    if (!open || ops.length) return;
    axios.get(`${API}/operacoes`).then((r) => setOps(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error('Erro ao carregar operações'));
  }, [open, ops.length]);

  const codigos = useMemo(() => {
    const set = new Map();
    ops.forEach((o) => { if (o.codigo) set.set(o.codigo.toUpperCase(), o.nome || ''); });
    sel.forEach((c) => { if (!set.has(c)) set.set(c, ''); });
    return [...set.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ops, sel]);

  const toggle = (c) => setSel(sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c].sort());

  const guardar = async () => {
    if (sel.length === 0) { toast.error('Seleccione pelo menos uma operação'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/relatorios/ranking-final/config`, { operacoes_acabadas: sel, meta_alvo: meta === '' ? null : Number(meta), mensagem_equipa: msg, orientacao_ia: orientacao });
      toast.success('Configuração guardada');
      onSaved(res.data);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const lista = codigos.filter(([c, n]) => !filtro || c.includes(filtro.toUpperCase()) || n.toLowerCase().includes(filtro.toLowerCase()));
  return (
    <Card className="p-3" data-testid="rf-config-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left" data-testid="rf-config-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold text-navy"><Settings2 className="h-4 w-4" /> Configuração do Ranking Final</span>
        <span className="flex items-center gap-3 text-xs text-stone-500">
          <span>Peças acabadas: <strong className="text-navy">({(config?.operacoes_acabadas || []).join(' + ')}) / {(config?.operacoes_acabadas || []).length || 1}</strong></span>
          <span>Meta: <strong className="text-navy">{config?.meta_alvo ? `${config.meta_alvo}%` : '—'}</strong></span>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Operações que contam como "peças acabadas"</Label>
            <p className="text-[11px] text-stone-500 mb-2">Peças acabadas = soma das quantidades das operações seleccionadas ÷ nº de operações seleccionadas.</p>
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[1.75rem]">
              {sel.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid={`rf-config-op-sel-${c}`}>
                  {c}<button type="button" onClick={() => toggle(c)} className="p-0.5 rounded-full hover:bg-white/20"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {sel.length === 0 && <span className="text-xs text-red-600">Nenhuma operação seleccionada</span>}
            </div>
            <Input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Pesquisar código ou nome..." className="h-8 text-xs mb-2" data-testid="rf-config-op-filtro" />
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto border rounded-lg p-2">
              {lista.map(([c, n]) => (
                <button key={c} type="button" onClick={() => toggle(c)} title={n} data-testid={`rf-config-op-${c}`}
                  className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${sel.includes(c) ? 'bg-emerald-100 border-emerald-300 text-emerald-800 font-semibold' : 'bg-white border-stone-300 text-stone-600 hover:bg-stone-50'}`}>
                  {sel.includes(c) ? '✓ ' : ''}{c}{n ? <span className="text-stone-400 font-normal"> · {n.slice(0, 22)}</span> : null}
                </button>
              ))}
              {lista.length === 0 && <span className="text-xs text-stone-400">{ops.length ? 'Sem resultados' : 'A carregar operações...'}</span>}
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Meta alvo de eficiência da equipa (%)</Label>
              <Input type="number" min="0" max="500" step="1" value={meta} onChange={(e) => setMeta(e.target.value)} className="h-9 w-40" data-testid="rf-config-meta-input" />
            </div>
            <div>
              <Label className="text-xs">Orientação para a IA (opcional)</Label>
              <p className="text-[10px] text-stone-500 mb-1">Escreve o rumo/tema que a mensagem deve seguir. A IA respeita esta indicação ao gerar. Fica guardada.</p>
              <Textarea value={orientacao} onChange={(e) => setOrientacao(e.target.value.slice(0, 300))} rows={2} placeholder="ex: Reforçar pontualidade e segurança; agradecer o esforço; motivar para chegar aos 85%." data-testid="rf-config-orientacao-input" />
              <p className="text-[10px] text-stone-400 text-right">{orientacao.length}/300</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1 gap-2">
                <Label className="text-xs">Mensagem à equipa (aparece no PDF)</Label>
                <div className="flex items-center gap-1.5">
                  <select
                    value={tomIA}
                    onChange={(e) => setTomIA(e.target.value)}
                    className="h-7 text-[11px] border rounded-md px-1.5 bg-white"
                    disabled={suggesting}
                    title="Tom da mensagem gerada pela IA"
                    data-testid="rf-config-mensagem-tom"
                  >
                    <option value="motivador">Motivador</option>
                    <option value="direto">Direto</option>
                    <option value="caloroso">Caloroso</option>
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px] gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={sugerirComIA}
                    disabled={suggesting || (semanas || []).length === 0}
                    data-testid="rf-config-mensagem-ia-btn"
                    title="Sugerir mensagem com IA baseada nos dados da semana"
                  >
                    {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {suggesting ? `A gerar... ${elapsed}s` : 'Sugerir com IA'}
                  </Button>
                </div>
              </div>
              <Textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, MSG_MAX))} maxLength={MSG_MAX} rows={3} placeholder="ex: Bom nível esta semana (81.5%). Foco: manter o ritmo e chegar aos 85%." data-testid="rf-config-mensagem-input" />
              <p className={`text-[10px] text-right ${msg.length >= MSG_MAX ? 'text-amber-600 font-semibold' : 'text-stone-400'}`} data-testid="rf-config-mensagem-contador">{msg.length}/{MSG_MAX}{msg.length > MSG_MAX ? ' — será encurtada ao guardar' : ''}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
              <Button size="sm" onClick={guardar} disabled={saving} data-testid="rf-config-guardar-btn">{saving ? 'A guardar...' : 'Guardar configuração'}</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// Mesmas regras do backend: sem / \ : * ? " < > |, sem ".pdf" no fim, máx. 120 chars
const limparNomePdf = (nome) => (nome || '')
  .trim()
  .replace(/\.pdf$/i, '')
  .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '-')
  .replace(/\s+/g, ' ')
  .replace(/^[\s.-]+|[\s.-]+$/g, '')
  .slice(0, 120);

const fmtDataHora = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const HistoricoPDFs = ({ refreshKey, onUsarSemanas, onTotal }) => {
  const open = true;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/relatorios/ranking-final/pdfs`, { params: { limite: 100 } });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      if (onTotal) onTotal(res.data?.total || 0);
    } catch {
      toast.error('Erro ao carregar histórico de PDFs');
    } finally {
      setLoading(false);
    }
  }, [onTotal]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  const obterBlob = async (it) => {
    const res = await axios.get(`${API}/relatorios/ranking-final/pdfs/${it.id}/download`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  };

  const abrir = async (it) => {
    const w = window.open('', '_blank'); // abrir já (evita bloqueio de pop-ups)
    setBusyId(it.id);
    try {
      const url = await obterBlob(it);
      if (w) w.location.href = url; else window.location.href = url;
    } catch {
      if (w) w.close();
      toast.error('Erro ao abrir PDF');
    } finally { setBusyId(null); }
  };

  const descarregar = async (it) => {
    setBusyId(it.id);
    try {
      const url = await obterBlob(it);
      const a = document.createElement('a');
      a.href = url; a.download = it.nome_ficheiro || 'ranking_final.pdf'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error('Erro ao descarregar PDF');
    } finally { setBusyId(null); }
  };

  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState('');
  const iniciarRenomear = (it) => { setEditId(it.id); setEditNome((it.nome_ficheiro || '').replace(/\.pdf$/i, '')); };
  const guardarNome = async (it) => {
    const nome = limparNomePdf(editNome);
    if (!nome) { toast.error('Indique um nome para o ficheiro'); return; }
    setBusyId(it.id);
    try {
      const res = await axios.patch(`${API}/relatorios/ranking-final/pdfs/${it.id}`, { nome_ficheiro: nome });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, nome_ficheiro: res.data?.nome_ficheiro || `${nome}.pdf` } : x)));
      setEditId(null);
      toast.success('Nome do ficheiro alterado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao mudar o nome');
    } finally { setBusyId(null); }
  };

  const eliminar = async (it) => {
    if (!window.confirm(`Eliminar o PDF gerado em ${fmtDataHora(it.criado_em)} do histórico?`)) return;
    setBusyId(it.id);
    try {
      await axios.delete(`${API}/relatorios/ranking-final/pdfs/${it.id}`);
      toast.success('PDF eliminado do histórico');
      carregar();
    } catch {
      toast.error('Erro ao eliminar PDF');
    } finally { setBusyId(null); }
  };

  return (
    <Card className="p-3" data-testid="rf-historico-pdfs">
      <div className="w-full flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-navy">
          <History className="h-4 w-4" /> Histórico de PDFs gerados
          <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-[11px] font-semibold" data-testid="rf-historico-count">{total}</span>
        </span>
        <span className="flex items-center gap-2 text-xs text-stone-500">
          {items[0] && <span>Último: <strong className="text-navy">{fmtDataHora(items[0].criado_em)}</strong></span>}
        </span>
      </div>
      <p className="text-[11px] text-stone-500 mt-1">Registo de todos os cartazes PDF gerados. Apenas para consulta — não altera a análise.</p>
      {open && (
        <div className="mt-3 overflow-x-auto">
          {loading && items.length === 0 ? (
            <p className="text-xs text-stone-400 py-4 text-center">A carregar...</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-stone-400 py-4 text-center">Ainda não foram gerados PDFs. Cada vez que clicar em <strong>PDF Samidel</strong> fica aqui uma cópia.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-stone-500 border-b">
                  <th className="text-left py-2 px-2">Ficheiro</th>
                  <th className="text-left py-2 px-2">Gerado em</th>
                  <th className="text-left py-2 px-2">Por</th>
                  <th className="text-left py-2 px-2">Semanas</th>
                  <th className="text-center py-2 px-2">Dias</th>
                  <th className="text-center py-2 px-2">Produtividade</th>
                  <th className="text-left py-2 px-2">Mensagem</th>
                  <th className="text-right py-2 px-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b last:border-0 hover:bg-stone-50" data-testid={`rf-historico-row-${it.id}`}>
                    <td className="py-2 px-2 min-w-[220px]">
                      {editId === it.id ? (
                        <div className="flex items-center gap-1">
                          <Input value={editNome} onChange={(e) => setEditNome(e.target.value.slice(0, 120))} autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') guardarNome(it); if (e.key === 'Escape') setEditId(null); }}
                            className="h-7 text-xs" data-testid={`rf-historico-nome-input-${it.id}`} />
                          <span className="text-stone-400">.pdf</span>
                          <Button size="sm" className="h-7 px-2" onClick={() => guardarNome(it)} disabled={busyId === it.id} data-testid={`rf-historico-nome-guardar-${it.id}`}>OK</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => setEditId(null)} title="Cancelar"><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 group">
                          <FileText className="h-3.5 w-3.5 text-red-500 shrink-0" />
                          <span className="font-medium text-stone-800 truncate max-w-[260px]" title={it.nome_ficheiro} data-testid={`rf-historico-nome-${it.id}`}>{it.nome_ficheiro || '—'}</span>
                          <button type="button" onClick={() => iniciarRenomear(it)} className="p-1 rounded text-stone-400 hover:text-navy hover:bg-stone-100" title="Mudar o nome do ficheiro" data-testid={`rf-historico-renomear-${it.id}`}>
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap text-navy">{fmtDataHora(it.criado_em)}</td>
                    <td className="py-2 px-2 whitespace-nowrap text-stone-600">{it.criado_por_nome || '—'}</td>
                    <td className="py-2 px-2">
                      <div className="flex flex-wrap gap-1">
                        {(it.semanas || []).map((m) => (
                          <span key={m} className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 text-[10px] font-medium whitespace-nowrap">
                            {m.slice(8, 10)}/{m.slice(5, 7)}–{addDays(m, 6).slice(8, 10)}/{addDays(m, 6).slice(5, 7)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center text-stone-600 whitespace-nowrap">{(it.dias || []).map((d) => DIAS[d]).join(' ')}</td>
                    <td className="py-2 px-2 text-center">
                      {it.eficiencia_equipa != null ? (
                        <span className="inline-block px-2 py-0.5 rounded-md font-bold" style={{ backgroundColor: it.nivel_cor || '#e7e5e4', color: ['#65a30d', '#2563eb', '#b91c1c'].includes(it.nivel_cor) ? '#fff' : '#1c1917' }} title={it.nivel_nome || ''}>
                          {it.eficiencia_equipa}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-2 text-stone-500 max-w-[220px] truncate" title={it.mensagem_equipa || ''}>{it.mensagem_equipa || '—'}</td>
                    <td className="py-2 px-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => abrir(it)} disabled={busyId === it.id} title="Abrir PDF" data-testid={`rf-historico-abrir-${it.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => descarregar(it)} disabled={busyId === it.id} title="Descarregar" data-testid={`rf-historico-download-${it.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => onUsarSemanas(it)} title="Carregar estas semanas e dias na análise" data-testid={`rf-historico-usar-${it.id}`}>Usar semanas</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => eliminar(it)} disabled={busyId === it.id} title="Eliminar do histórico" data-testid={`rf-historico-eliminar-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
};

const fmtPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1).replace(/\.0$/, '')}%`);

const DifBadge = ({ dif }) => {
  if (dif === null || dif === undefined) return null;
  const cls = dif > 0 ? 'bg-emerald-100 text-emerald-700' : dif < 0 ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-500';
  return <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>{dif > 0 ? '+' : ''}{dif.toFixed(1).replace(/\.0$/, '')}</span>;
};

// Lista de todos os dias ajustados (valor sugerido) — separador próprio, só consulta/gestão
const ListaAjustes = ({ refreshKey, onEditar, onAbrirSemana, onTotal, onChanged }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtroOp, setFiltroOp] = useState('todos');
  const [dataIni, setDataIni] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [pesquisa, setPesquisa] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dataIni) params.data_inicio = dataIni;
      if (dataFim) params.data_fim = dataFim;
      const res = await axios.get(`${API}/relatorios/ranking-final/ajustes`, { params });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      if (onTotal && !dataIni && !dataFim) onTotal(res.data?.total || 0);
    } catch {
      toast.error('Erro ao carregar ajustes');
    } finally {
      setLoading(false);
    }
  }, [dataIni, dataFim, onTotal]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  const operadores = useMemo(() => {
    const m = new Map();
    items.forEach((i) => m.set(i.operador_id, i.operador_nome));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const visiveis = items.filter((i) => (
    (filtroOp === 'todos' || i.operador_id === filtroOp)
    && (!pesquisa.trim() || `${i.operador_nome} ${i.motivo || ''}`.toLowerCase().includes(pesquisa.trim().toLowerCase()))
  ));

  const repor = async (it) => {
    if (!window.confirm(`Repor o valor real de ${it.operador_nome} em ${it.data.slice(8, 10)}/${it.data.slice(5, 7)}/${it.data.slice(0, 4)}? O ajuste será apagado.`)) return;
    const key = `${it.operador_id}-${it.data}`;
    setBusyKey(key);
    try {
      await axios.put(`${API}/relatorios/ranking-final/ajuste`, { operador_id: it.operador_id, data: it.data, eficiencia: null, ocupacao: null, motivo: null });
      toast.success('Valor real reposto');
      carregar();
      if (onChanged) onChanged(); // a Análise tem de refletir o valor real ao voltar
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao repor valor real');
    } finally { setBusyKey(null); }
  };

  const verRegistos = (it) => {
    const qs = new URLSearchParams({ tab: 'operador', operador: it.operador_id, data: it.data });
    window.open(`/gestor/relatorios?${qs.toString()}`, '_blank', 'noopener');
  };

  return (
    <Card className="p-3" data-testid="rf-lista-ajustes">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="flex items-center gap-2 text-sm font-semibold text-navy">
            <Pencil className="h-4 w-4" /> Dias ajustados
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold" data-testid="rf-ajustes-count">{total}</span>
          </span>
          <p className="text-[11px] text-stone-500 mt-1">Todos os valores sugeridos (eficiência / ocupação) que substituem o valor real no Ranking Final.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={filtroOp} onChange={(e) => setFiltroOp(e.target.value)} className="h-8 border rounded-lg text-xs px-2" data-testid="rf-ajustes-filtro-operador">
            <option value="todos">Todos os operadores</option>
            {operadores.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </select>
          <input type="date" value={dataIni} onChange={(e) => setDataIni(e.target.value)} className="h-8 px-2 border rounded-lg text-xs" title="Desde" data-testid="rf-ajustes-data-inicio" />
          <span className="text-xs text-stone-400">até</span>
          <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="h-8 px-2 border rounded-lg text-xs" title="Até" data-testid="rf-ajustes-data-fim" />
          <Input value={pesquisa} onChange={(e) => setPesquisa(e.target.value)} placeholder="Pesquisar operador ou motivo..." className="h-8 w-56 text-xs" data-testid="rf-ajustes-pesquisa" />
          {(filtroOp !== 'todos' || dataIni || dataFim || pesquisa) && (
            <button type="button" onClick={() => { setFiltroOp('todos'); setDataIni(''); setDataFim(''); setPesquisa(''); }} className="text-xs text-stone-500 hover:text-navy underline">Limpar</button>
          )}
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        {loading && items.length === 0 ? (
          <p className="text-xs text-stone-400 py-6 text-center">A carregar...</p>
        ) : visiveis.length === 0 ? (
          <p className="text-xs text-stone-400 py-6 text-center">{items.length ? 'Nenhum ajuste com estes filtros.' : 'Ainda não há dias ajustados. Clique numa célula da tabela semanal (separador Análise) para sugerir um valor.'}</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-stone-500 border-b">
                <th className="text-left py-2 px-2">Dia</th>
                <th className="text-left py-2 px-2">Operador</th>
                <th className="text-center py-2 px-2">Eficiência<br /><span className="normal-case font-normal">real → sugerida</span></th>
                <th className="text-center py-2 px-2">Ocupação<br /><span className="normal-case font-normal">real → sugerida</span></th>
                <th className="text-left py-2 px-2">Motivo</th>
                <th className="text-left py-2 px-2">Alterado</th>
                <th className="text-right py-2 px-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((it) => {
                const key = `${it.operador_id}-${it.data}`;
                return (
                  <tr key={key} className="border-b last:border-0 hover:bg-amber-50/40" data-testid={`rf-ajuste-row-${key}`}>
                    <td className="py-2 px-2 whitespace-nowrap"><span className="font-semibold text-navy">{it.dia_semana}</span> {it.data.slice(8, 10)}/{it.data.slice(5, 7)}/{it.data.slice(0, 4)}</td>
                    <td className="py-2 px-2 whitespace-nowrap font-medium text-stone-800">{it.operador_nome}</td>
                    <td className="py-2 px-2 text-center whitespace-nowrap tabular-nums">
                      {it.eficiencia !== null && it.eficiencia !== undefined ? (
                        <><span className="text-stone-400">{fmtPct(it.eficiencia_real)}</span> → <strong className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">{fmtPct(it.eficiencia)}</strong><DifBadge dif={it.diferenca_eficiencia} /></>
                      ) : <span className="text-stone-400">{fmtPct(it.eficiencia_real)} <span className="text-[10px]">(real)</span></span>}
                    </td>
                    <td className="py-2 px-2 text-center whitespace-nowrap tabular-nums">
                      {it.ocupacao !== null && it.ocupacao !== undefined ? (
                        <><span className="text-stone-400">{fmtPct(it.ocupacao_real)}</span> → <strong className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">{fmtPct(it.ocupacao)}</strong><DifBadge dif={it.diferenca_ocupacao} /></>
                      ) : <span className="text-stone-400">{fmtPct(it.ocupacao_real)} <span className="text-[10px]">(real)</span></span>}
                    </td>
                    <td className="py-2 px-2 text-stone-600 max-w-[240px] truncate" title={it.motivo || ''}>{it.motivo || <span className="text-stone-300">sem motivo</span>}</td>
                    <td className="py-2 px-2 whitespace-nowrap text-stone-500">{fmtDataHora(it.atualizado_em)}{it.atualizado_por_nome ? <span className="block text-[10px]">{it.atualizado_por_nome}</span> : null}</td>
                    <td className="py-2 px-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2" title="Editar ajuste" onClick={() => onEditar(it)} data-testid={`rf-ajuste-editar-${key}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" title="Ver registos do dia (nova aba)" onClick={() => verRegistos(it)} data-testid={`rf-ajuste-registos-${key}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" title="Abrir esta semana na análise" onClick={() => onAbrirSemana(it)} data-testid={`rf-ajuste-semana-${key}`}>Ver semana</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" title="Repor valor real (apaga o ajuste)" disabled={busyKey === key} onClick={() => repor(it)} data-testid={`rf-ajuste-repor-${key}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
};

export const RankingFinal = () => {
  const [semanas, setSemanas] = useState(() => loadLS(LS_SEMANAS, [toMonday(fmtISO(new Date()))]));
  const [dias, setDias] = useState(() => loadLS(LS_DIAS, [0, 1, 2, 3, 4]));
  const [novaData, setNovaData] = useState('');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ key: 'eficiencia', dir: 'desc' });
  const [ajuste, setAjuste] = useState(null);
  const [histKey, setHistKey] = useState(0);
  const [vista, setVista] = useState('analise'); // 'analise' | 'ajustes' | 'historico'
  const [histTotal, setHistTotal] = useState(null);
  const [ajustesTotal, setAjustesTotal] = useState(null);
  const [ajustesKey, setAjustesKey] = useState(0);

  // Contador do separador "Ajustes"
  useEffect(() => {
    axios.get(`${API}/relatorios/ranking-final/ajustes`, { params: { limite: 1 } })
      .then((r) => setAjustesTotal(r.data?.total ?? 0))
      .catch(() => {});
  }, [ajustesKey]);

  // Contador do separador "Histórico de PDFs" (pedido leve, só o total)
  useEffect(() => {
    axios.get(`${API}/relatorios/ranking-final/pdfs`, { params: { limite: 1 } })
      .then((r) => setHistTotal(r.data?.total ?? 0))
      .catch(() => {});
  }, [histKey]);

  useEffect(() => { localStorage.setItem(LS_SEMANAS, JSON.stringify(semanas)); }, [semanas]);
  useEffect(() => { localStorage.setItem(LS_DIAS, JSON.stringify(dias)); }, [dias]);

  const reqIdRef = useRef(0);
  const carregar = useCallback(async () => {
    if (semanas.length === 0) { setDados(null); return; }
    const reqId = ++reqIdRef.current; // ignora respostas de pedidos antigos (cliques rápidos)
    setLoading(true);
    try {
      const res = await axios.get(`${API}/relatorios/ranking-final`, { params: { semanas: semanas.join(','), dias: dias.join(',') } });
      if (reqId === reqIdRef.current) setDados(res.data);
    } catch (e) {
      if (reqId === reqIdRef.current) toast.error('Erro ao carregar ranking final');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [semanas, dias]);

  useEffect(() => { carregar(); }, [carregar]);

  const adicionarSemana = (iso) => {
    if (!iso) return;
    const m = toMonday(iso);
    if (semanas.includes(m)) { toast.info('Semana já adicionada'); return; }
    setSemanas([...semanas, m].sort());
    setNovaData('');
  };
  const removerSemana = (m) => setSemanas(semanas.filter((s) => s !== m));
  const toggleDia = (i) => {
    if (dias.includes(i) && dias.length === 1) {
      // Sem nenhum dia o backend assumia Seg–Sex por omissão (a tabela não batia com os botões)
      toast.info('Tem de ficar pelo menos um dia incluído no cálculo');
      return;
    }
    setDias(dias.includes(i) ? dias.filter((d) => d !== i) : [...dias, i].sort());
  };
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'nome' ? 'asc' : 'desc' }));

  // Nome sugerido para o PDF (editável antes de gerar)
  const [nomeDialogOpen, setNomeDialogOpen] = useState(false);
  const [nomePdf, setNomePdf] = useState('');
  const sugerirNomePdf = () => {
    if (!semanas.length) return 'Ranking Final';
    const ord = [...semanas].sort();
    const ini = ord[0];
    const fim = addDays(ord[ord.length - 1], 6);
    return `Ranking Final ${ini.slice(8, 10)}-${ini.slice(5, 7)} a ${fim.slice(8, 10)}-${fim.slice(5, 7)}-${fim.slice(0, 4)}`;
  };
  const abrirDialogNomePdf = () => { setNomePdf(sugerirNomePdf()); setNomeDialogOpen(true); };

  const exportarPDF = async (nomeEscolhido) => {
    setExporting(true);
    try {
      const nomeLimpo = limparNomePdf(nomeEscolhido) || sugerirNomePdf();
      const res = await axios.get(`${API}/relatorios/ranking-final/pdf`, { params: { semanas: semanas.join(','), dias: dias.join(','), nome: nomeLimpo }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${nomeLimpo}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setNomeDialogOpen(false);
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado e guardado no histórico');
      setHistKey((k) => k + 1);
    } catch (e) {
      // A resposta é um blob — ler o detalhe do erro enviado pelo servidor
      let detalhe = '';
      try {
        const blob = e.response?.data;
        if (blob && typeof blob.text === 'function') detalhe = JSON.parse(await blob.text())?.detail || '';
      } catch { /* resposta não-JSON */ }
      toast.error(detalhe ? `Erro ao gerar PDF: ${detalhe}` : 'Erro ao gerar PDF');
    } finally {
      setExporting(false);
    }
  };

  const hojeMonday = toMonday(fmtISO(new Date()));

  const separadores = (
    <div className="inline-flex p-1 rounded-xl bg-stone-100 border border-stone-200" role="tablist" data-testid="rf-subtabs">
      <button type="button" role="tab" aria-selected={vista === 'analise'} onClick={() => setVista('analise')} data-testid="rf-subtab-analise"
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${vista === 'analise' ? 'bg-white text-navy shadow-sm' : 'text-stone-500 hover:text-navy'}`}>
        Análise
      </button>
      <button type="button" role="tab" aria-selected={vista === 'ajustes'} onClick={() => setVista('ajustes')} data-testid="rf-subtab-ajustes"
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${vista === 'ajustes' ? 'bg-white text-navy shadow-sm' : 'text-stone-500 hover:text-navy'}`}>
        <Pencil className="h-3.5 w-3.5" /> Ajustes
        {ajustesTotal != null && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="rf-subtab-ajustes-count">{ajustesTotal}</span>}
      </button>
      <button type="button" role="tab" aria-selected={vista === 'historico'} onClick={() => setVista('historico')} data-testid="rf-subtab-historico"
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${vista === 'historico' ? 'bg-white text-navy shadow-sm' : 'text-stone-500 hover:text-navy'}`}>
        <History className="h-3.5 w-3.5" /> Histórico de PDFs
        {histTotal != null && <span className="px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-600 text-[10px] font-semibold" data-testid="rf-subtab-historico-count">{histTotal}</span>}
      </button>
    </div>
  );

  if (vista === 'ajustes') {
    return (
      <div className="space-y-4" data-testid="ranking-final-tab">
        {separadores}
        <ListaAjustes
          refreshKey={ajustesKey}
          onTotal={setAjustesTotal}
          onChanged={carregar}
          onEditar={(it) => setAjuste({
            operador: { id: it.operador_id, nome: it.operador_nome },
            data: it.data,
            celula: {
              tem_dados: it.tem_dados,
              eficiencia_real: it.eficiencia_real, ocupacao_real: it.ocupacao_real,
              eficiencia_sugerida: it.eficiencia, ocupacao_sugerida: it.ocupacao,
              motivo: it.motivo, pecas: it.pecas, horas: it.horas,
            },
          })}
          onAbrirSemana={(it) => {
            setSemanas((prev) => (prev.includes(it.semana_inicio) ? prev : [...prev, it.semana_inicio].sort()));
            setVista('analise');
            toast.success('Semana aberta na análise');
          }}
        />
        <AjusteDialog ajuste={ajuste} onClose={() => setAjuste(null)} onSaved={() => { setAjustesKey((k) => k + 1); carregar(); }} />
      </div>
    );
  }

  if (vista === 'historico') {
    return (
      <div className="space-y-4" data-testid="ranking-final-tab">
        {separadores}
        <HistoricoPDFs
          refreshKey={histKey}
          onTotal={setHistTotal}
          onUsarSemanas={(it) => {
            if (it.semanas?.length) setSemanas([...it.semanas].sort());
            if (it.dias?.length) setDias([...it.dias].sort());
            setVista('analise');
            toast.success('Semanas e dias do PDF carregados na análise');
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ranking-final-tab">
      {separadores}
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <p className="text-xs font-semibold text-stone-600 mb-2">Semanas em análise</p>
            <div className="flex flex-wrap items-center gap-2">
              {semanas.map((m) => (
                <span key={m} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-navy text-white text-xs font-medium" data-testid={`rf-semana-chip-${m}`}>
                  {m.slice(8, 10)}/{m.slice(5, 7)} – {addDays(m, 6).slice(8, 10)}/{addDays(m, 6).slice(5, 7)}
                  <button type="button" onClick={() => removerSemana(m)} className="p-0.5 rounded-full hover:bg-white/20" title="Remover semana" data-testid={`rf-remover-semana-${m}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
              {semanas.length === 0 && <span className="text-xs text-stone-400">Nenhuma semana seleccionada</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <input type="date" value={novaData} onChange={(e) => setNovaData(e.target.value)} className="h-8 px-2 border rounded-lg text-xs" data-testid="rf-nova-semana-input" />
              <Button size="sm" variant="outline" className="h-8" onClick={() => adicionarSemana(novaData)} disabled={!novaData} data-testid="rf-adicionar-semana-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Semana</Button>
              <button type="button" onClick={() => adicionarSemana(hojeMonday)} className="text-xs px-2.5 py-1.5 rounded-lg bg-stone-100 hover:bg-emerald-100 hover:text-emerald-700 font-medium" data-testid="rf-semana-atual-btn">Semana actual</button>
              <button type="button" onClick={() => adicionarSemana(addDays(semanas.length ? semanas[0] : hojeMonday, -7))} className="text-xs px-2.5 py-1.5 rounded-lg bg-stone-100 hover:bg-emerald-100 hover:text-emerald-700 font-medium" data-testid="rf-semana-anterior-btn">+ Semana anterior</button>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-stone-600 mb-2">Dias incluídos no cálculo semanal</p>
            <div className="flex gap-1.5">
              {DIAS.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDia(i)} data-testid={`rf-dia-toggle-${i}`}
                  className={`w-10 h-8 rounded-lg text-xs font-semibold border transition-colors ${dias.includes(i) ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-stone-300 text-stone-400 hover:bg-stone-50'}`}>
                  {d}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-stone-400 mt-1">Média simples dos dias marcados · valor sugerido substitui o real</p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button variant="outline" size="sm" onClick={abrirDialogNomePdf} disabled={exporting || semanas.length === 0} data-testid="rf-export-pdf-btn">
              <FileText className="h-4 w-4 mr-1" /> {exporting ? 'A gerar...' : 'PDF Samidel'}
            </Button>
            <div className="flex items-center gap-1 text-xs text-stone-500">
              Ordenar:
              <select value={sort.key.startsWith('dia:') ? 'eficiencia' : sort.key} onChange={(e) => setSort({ key: e.target.value, dir: e.target.value === 'nome' ? 'asc' : 'desc' })} className="h-7 border rounded-md text-xs px-1" data-testid="rf-sort-select">
                <option value="eficiencia">Eficiência semana</option>
                <option value="ocupacao">Ocupação semana</option>
                <option value="pecas_acabadas">Peças acabadas</option>
                <option value="nome">Nome</option>
              </select>
              <button type="button" onClick={() => setSort((s) => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))} className="p-1 rounded hover:bg-stone-100" title="Inverter ordem" data-testid="rf-sort-dir-btn">
                {sort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </Card>

      <ConfigPanel config={dados?.config} onSaved={carregar} semanas={semanas} dias={dias} />

      {loading && !dados && <div className="text-center py-12 text-stone-500">A carregar ranking final...</div>}
      {dados && dados.semanas.length > 0 && <ResumoEvolucao semanas={dados.semanas} niveis={dados.niveis} config={dados.config} onSaved={carregar} />}
      {dados && [...dados.semanas].reverse().map((s, i, arr) => (
        <SemanaTabela key={s.inicio} semana={s} index={i} total={arr.length} sort={sort} onSort={onSort} onEditar={setAjuste} opsAcabadas={dados.operacoes_acabadas} />
      ))}
      {dados && dados.semanas.length === 1 && (
        <div className="text-xs text-stone-500 px-1">Adicione mais semanas para comparar a evolução da produtividade.</div>
      )}
      <AjusteDialog ajuste={ajuste} onClose={() => setAjuste(null)} onSaved={() => { setAjustesKey((k) => k + 1); carregar(); }} />

      {/* Nome do ficheiro PDF (sugerido, editável) */}
      <Dialog open={nomeDialogOpen} onOpenChange={(v) => !v && !exporting && setNomeDialogOpen(false)}>
        <DialogContent className="max-w-md" data-testid="rf-pdf-nome-dialog">
          <DialogHeader>
            <DialogTitle>Gerar PDF Samidel</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Nome do ficheiro</Label>
            <div className="flex items-center gap-1.5">
              <Input value={nomePdf} onChange={(e) => setNomePdf(e.target.value.slice(0, 120))} autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && !exporting) exportarPDF(nomePdf); }}
                className="h-9 text-sm" data-testid="rf-pdf-nome-input" />
              <span className="text-sm text-stone-500">.pdf</span>
            </div>
            <p className="text-[11px] text-stone-500">Fica com este nome no download e no separador Histórico de PDFs (pode mudar depois).</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNomeDialogOpen(false)} disabled={exporting}>Cancelar</Button>
            <Button onClick={() => exportarPDF(nomePdf)} disabled={exporting} data-testid="rf-pdf-gerar-btn">
              <FileText className="h-4 w-4 mr-1" /> {exporting ? 'A gerar...' : 'Gerar PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RankingFinal;
